import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Maximize2, Minimize2, Save, ZoomIn, ZoomOut } from 'lucide-react';
import { PartCategory, PartType, GeometricProperties, CalculatedProperties } from '../../types/studio';
import { calculateProperties, GAUGE_MAP, BRACKET_MATERIALS, BOLT_MATERIALS_MAP, PIN_MATERIALS_MAP } from '../../utils/engineeringUtils';
import PartPreview from '../../components/studio/PartPreview';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
// We use a custom Label comp for consistency with the original design but using standard classes
const Label = ({ children, className }: { children?: React.ReactNode, className?: string }) => (
    <label className={cn("block text-xs text-muted-foreground mb-1.5 font-medium", className)}>{children}</label>
);

const DataRow = ({ label, value, unit }: { label: string, value: number, unit: string }) => (
    <div className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
        <span className="text-muted-foreground text-sm">{label}</span>
        <span className="font-mono text-sm text-foreground font-medium text-right">{value} <span className="text-muted-foreground text-xs">{unit}</span></span>
    </div>
);

// Helper for native select with ShadCN styling
const NativeSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div className="relative">
        <select
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none pr-8",
            className
          )}
          ref={ref}
          {...props}
        />
        <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 opacity-50 pointer-events-none" />
      </div>
    );
  }
);
NativeSelect.displayName = "NativeSelect";

const PartBuilder = () => {
  // --- STATE ---
  const [partName, setPartName] = useState('');
  const [category, setCategory] = useState<PartCategory>(PartCategory.COLUMN);
  const [type, setType] = useState<PartType>(PartType.CLOSED_TUBE); 
  
  // Dimensions
  const [geometry, setGeometry] = useState<GeometricProperties>({
    h: 5,
    w: 5,
    t: 0.2242, // Default Gage 4
    l: 0.75,
    hw: 0.75,
    d: 1.625, // Default Step Height
    D: 1.625  // Default Top Flange Width
  });

  const [gage, setGage] = useState<string>('4');
  const [isCustomGage, setIsCustomGage] = useState(false);
  
  // Material State
  const [material, setMaterial] = useState<string>('Unknown');
  const [typeOfSteel, setTypeOfSteel] = useState<string>('Unknown');
  
  // Custom Material Props
  const [customFy, setCustomFy] = useState<number>(0);
  const [customFu, setCustomFu] = useState<number>(0);

  // Calculated
  const [props, setProps] = useState<CalculatedProperties>({
    Ix: 0, Sx: 0, Rx: 0, Iy: 0, Syl: 0, Syr: 0, Ry: 0,
    grossArea: 0, netArea: 0,
    cCorner: 0, J: 0, Xo: 0, jx: 0, Ro: 0, Beta: 0, Cw: 0, weight: 0, Fy: 0, Fu: 0
  });

  const [techDataOpen, setTechDataOpen] = useState(true);

  // Preview Scale & Screen State
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));

  // --- EFFECTS ---

  // Handle Gage Selection
  useEffect(() => {
    if (gage !== 'Custom') {
      const t = GAUGE_MAP[gage] || 0.2242;
      setGeometry(prev => ({ ...prev, t }));
      setIsCustomGage(false);
    } else {
      setIsCustomGage(true);
    }
  }, [gage]);

  // Handle Category Switch defaults
  useEffect(() => {
    if (category === PartCategory.COLUMN) {
        if (![PartType.CLOSED_TUBE, PartType.C_CHANNEL_LIPS, PartType.C_CHANNEL_LIPS_RIB].includes(type)) {
            setType(PartType.CLOSED_TUBE);
        }
    } else if (category === PartCategory.BEAM) {
        if (type !== PartType.STEP_BEAM) setType(PartType.STEP_BEAM);
    } else if (category === PartCategory.BRACE) {
        if (![PartType.C_CHANNEL, PartType.C_CHANNEL_LIPS, PartType.ANGLE, PartType.PIPE, PartType.CLOSED_TUBE].includes(type)) {
             setType(PartType.C_CHANNEL);
        }
    } else if (category === PartCategory.CONNECTION) {
        if (![PartType.BRACKET, PartType.BOLT, PartType.PIN, PartType.TAB].includes(type)) {
             setType(PartType.BRACKET);
        }
        setMaterial('Unknown');
        setTypeOfSteel('Unknown');
        setCustomFy(0);
        setCustomFu(0);
    }
  }, [category, type]);

  // Recalculate
  useEffect(() => {
    const calculated = calculateProperties(type, geometry, material, { fy: customFy, fu: customFu });
    setProps(calculated);
  }, [geometry, type, material, customFy, customFu]);


  // --- HANDLERS ---
  const handleDimChange = (field: keyof GeometricProperties, val: string) => {
    const num = parseFloat(val);
    setGeometry(prev => ({ ...prev, [field]: isNaN(num) ? 0 : num }));
  };

  const isConnection = category === PartCategory.CONNECTION;

  return (
    <div className="flex-1 bg-background h-full flex flex-col font-sans">
        
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden h-[calc(100vh-64px)]">
        
        {/* LEFT PANEL: Builder Form */}
        <div className="w-full lg:w-[500px] lg:min-w-[400px] lg:max-w-[50%] bg-card border-r border-border overflow-y-auto flex flex-col custom-scrollbar h-1/2 lg:h-full shrink-0">
            
            {/* Header / Title */}
            <div className="px-6 pt-6 pb-2">
                 <h2 className="text-xl font-medium text-muted-foreground mb-6">The part</h2>
            </div>

            <div className="px-6 pb-10 space-y-6">
                
                {/* Row 1: Name, Dimensions */}
                <div className="grid grid-cols-12 gap-5">
                    <div className="col-span-12">
                        <Label>Part name</Label>
                        <Input 
                            type="text" 
                            value={partName} 
                            onChange={(e) => setPartName(e.target.value)} 
                        />
                    </div>

                    {/* Dimensions vary wildly by Type */}
                    {type === PartType.PIPE || type === PartType.BOLT || type === PartType.PIN ? (
                        <div className="col-span-6">
                            <Label>Diameter, d (in)</Label>
                            <Input 
                                type="number" step={0.1}
                                value={geometry.h} // Reuse h for Diameter
                                onChange={(e) => handleDimChange('h', e.target.value)}
                            />
                        </div>
                    ) : type === PartType.TAB ? (
                        <div className="col-span-6">
                             <Label>Width, ttab (in)</Label>
                             <Input 
                                type="number" step={0.1}
                                value={geometry.w} // Reuse w for Tab Width
                                onChange={(e) => handleDimChange('w', e.target.value)}
                             />
                        </div>
                    ) : (
                        <>
                        <div className="col-span-6">
                            <Label>Height, H (in)</Label>
                            <Input 
                                type="number" step={0.1}
                                value={geometry.h}
                                onChange={(e) => handleDimChange('h', e.target.value)}
                            />
                        </div>
                        <div className="col-span-6">
                            <Label>{type === PartType.BRACKET ? "Width, B (in)" : "Width, W (in)"}</Label>
                            <Input 
                                type="number" step={0.1}
                                value={geometry.w}
                                onChange={(e) => handleDimChange('w', e.target.value)}
                            />
                        </div>
                        </>
                    )}

                    {/* Gage/Thickness - Hidden for Bolt/Pin/Tab */}
                    {type !== PartType.BOLT && type !== PartType.PIN && type !== PartType.TAB && (
                    <div className="col-span-6">
                        <Label>Gage, t</Label>
                        <NativeSelect
                            value={gage}
                            onChange={(e) => setGage(e.target.value)}
                        >
                            <option value="Custom">Custom</option>
                            {Object.keys(GAUGE_MAP).sort((a,b) => Number(a)-Number(b)).map(g => (
                                <option key={g} value={g}>{g}</option>
                            ))}
                        </NativeSelect>
                    </div>
                    )}
                </div>

                {/* Row 2: Category, Thickness/Material */}
                <div className="grid grid-cols-12 gap-5">
                    <div className="col-span-6">
                        <Label>Part category</Label>
                        <NativeSelect
                            value={category}
                            onChange={(e) => setCategory(e.target.value as PartCategory)}
                        >
                            <option value={PartCategory.COLUMN}>Column</option>
                            <option value={PartCategory.BEAM}>Beam</option>
                            <option value={PartCategory.BRACE}>Brace</option>
                            <option value={PartCategory.CONNECTION}>Connection</option>
                        </NativeSelect>
                    </div>

                    {/* Conditional 2nd Field */}
                    <div className="col-span-6">
                        {isConnection ? (
                            // Material Dropdown for Connections
                            <>
                             <Label>Material</Label>
                             <NativeSelect
                                value={material}
                                onChange={(e) => setMaterial(e.target.value)}
                            >
                                <option value="Unknown">Unknown</option>
                                <option value="Custom">Custom</option>
                                
                                {type === PartType.BRACKET && BRACKET_MATERIALS.map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                                
                                {type === PartType.BOLT && Object.keys(BOLT_MATERIALS_MAP).filter(k=>k!=='Unknown').map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                                
                                {type === PartType.PIN && Object.keys(PIN_MATERIALS_MAP).filter(k=>k!=='Unknown').map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </NativeSelect>
                            </>
                        ) : (
                            // Thickness Input for Structural
                            <>
                            <Label>Thickness, t (in)</Label>
                            <Input 
                                type="number" 
                                className={cn(!isCustomGage && "bg-muted text-muted-foreground")}
                                value={geometry.t}
                                readOnly={!isCustomGage}
                                onChange={(e) => handleDimChange('t', e.target.value)}
                            />
                            </>
                        )}
                    </div>
                </div>

                {/* Row 3: Type, Conditional Fields */}
                <div className="grid grid-cols-12 gap-5">
                    <div className="col-span-12">
                         <Label>Part type</Label>
                         <NativeSelect
                            value={type}
                            onChange={(e) => setType(e.target.value as PartType)}
                        >
                            {category === PartCategory.COLUMN && (
                                <>
                                <option value={PartType.CLOSED_TUBE}>{PartType.CLOSED_TUBE}</option>
                                <option value={PartType.C_CHANNEL_LIPS}>{PartType.C_CHANNEL_LIPS}</option>
                                <option value={PartType.C_CHANNEL_LIPS_RIB}>{PartType.C_CHANNEL_LIPS_RIB}</option>
                                </>
                            )}
                            {category === PartCategory.BEAM && (
                                <option value={PartType.STEP_BEAM}>{PartType.STEP_BEAM}</option>
                            )}
                            {category === PartCategory.BRACE && (
                                <>
                                <option value={PartType.C_CHANNEL}>{PartType.C_CHANNEL}</option>
                                <option value={PartType.C_CHANNEL_LIPS}>{PartType.C_CHANNEL_LIPS}</option>
                                <option value={PartType.ANGLE}>{PartType.ANGLE}</option>
                                <option value={PartType.PIPE}>{PartType.PIPE}</option>
                                <option value={PartType.CLOSED_TUBE}>{PartType.CLOSED_TUBE}</option>
                                </>
                            )}
                            {category === PartCategory.CONNECTION && (
                                <>
                                <option value={PartType.BRACKET}>{PartType.BRACKET}</option>
                                <option value={PartType.BOLT}>{PartType.BOLT}</option>
                                <option value={PartType.PIN}>{PartType.PIN}</option>
                                <option value={PartType.TAB}>{PartType.TAB}</option>
                                </>
                            )}
                        </NativeSelect>
                    </div>
                    
                    <div className="col-span-12">
                        {(type === PartType.C_CHANNEL_LIPS || type === PartType.C_CHANNEL_LIPS_RIB) && (
                            <div className="w-1/2 pr-2">
                                <Label>Lip, L (in)</Label>
                                <Input 
                                    type="number" 
                                    value={geometry.l}
                                    onChange={(e) => handleDimChange('l', e.target.value)}
                                />
                            </div>
                        )}
                        {type === PartType.STEP_BEAM && (
                            <div className="w-1/2 pr-2">
                                <Label>Step Height, d (in)</Label>
                                <Input 
                                    type="number" 
                                    value={geometry.d}
                                    onChange={(e) => handleDimChange('d', e.target.value)}
                                />
                            </div>
                        )}
                        {/* Custom Material Props - Only visible if Custom is selected */}
                        {isConnection && material === 'Custom' && (
                            <div className="grid grid-cols-2 gap-5 mt-2">
                                <div>
                                    <Label>Yield, Fy (ksi)</Label>
                                    <Input 
                                        type="number" 
                                        value={customFy} 
                                        onChange={(e) => setCustomFy(parseFloat(e.target.value) || 0)} 
                                    />
                                </div>
                                <div>
                                    <Label>Tensile, Fu (ksi)</Label>
                                    <Input 
                                        type="number" 
                                        value={customFu}
                                        onChange={(e) => setCustomFu(parseFloat(e.target.value) || 0)} 
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Row 4: Type of Steel (Bracket) & Extra Dimensions */}
                {(type === PartType.BRACKET || type === PartType.STEP_BEAM || category === PartCategory.COLUMN) && (
                <div className="grid grid-cols-12 gap-5 mt-5">
                    {type === PartType.BRACKET && (
                        <div className="col-span-12">
                            <Label>Type of Steel</Label>
                            <NativeSelect
                                value={typeOfSteel}
                                onChange={(e) => setTypeOfSteel(e.target.value)}
                            >
                                <option value="Unknown">Unknown</option>
                                <option value="Roll formed">Roll formed</option>
                                <option value="Hot rolled">Hot rolled</option>
                            </NativeSelect>
                        </div>
                    )}
                    
                     {/* For Step Beam, we need Top Flange Width */}
                     {type === PartType.STEP_BEAM && (
                        <div className="col-span-6">
                            <Label>Top Flange Width, D (in)</Label>
                            <Input 
                                type="number" 
                                value={geometry.D}
                                onChange={(e) => handleDimChange('D', e.target.value)}
                            />
                        </div>
                     )}

                    {/* Standard Hole Width mostly for Columns */}
                    {category === PartCategory.COLUMN && (
                    <div className="col-span-6">
                        <Label>Hole Width, hw (in)</Label>
                        <Input 
                            type="number" 
                            value={geometry.hw}
                            onChange={(e) => handleDimChange('hw', e.target.value)}
                        />
                    </div>
                    )}
                </div>
                )}

                <div className="border-t border-border my-6"></div>

                {/* Technical Data Dropdown */}
                {type !== PartType.TAB && (
                <div>
                    <button 
                        className="flex items-center justify-between w-full text-left mb-6 focus:outline-none group"
                        onClick={() => setTechDataOpen(!techDataOpen)}
                    >
                        <h3 className="text-primary font-medium text-base">Technical data</h3>
                        {techDataOpen ? <ChevronUp size={20} className="text-primary/70" /> : <ChevronDown size={20} className="text-primary/70" />}
                    </button>

                    {techDataOpen && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-1">
                            {type === PartType.BOLT || type === PartType.PIN ? (
                                <>
                                <div>
                                    <DataRow label="Fu:" value={props.Fu} unit="(ksi)" />
                                    <DataRow label="Fnv:" value={props.Fnv || 0} unit="(ksi)" />
                                </div>
                                <div className="hidden md:block"></div>
                                </>
                            ) : type === PartType.BRACKET ? (
                                <>
                                <div>
                                    <DataRow label="Fy:" value={props.Fy} unit="(ksi)" />
                                    <DataRow label="Fu:" value={props.Fu} unit="(ksi)" />
                                    <DataRow label="Se:" value={props.Se || 0} unit="(in³)" />
                                </div>
                                <div className="hidden md:block"></div>
                                </>
                            ) : (
                            <>
                            {/* Left Column */}
                            <div>
                                <DataRow label="Full Ix:" value={props.Ix} unit="(in⁴)" />
                                <DataRow label="Full Sx:" value={props.Sx} unit="(in³)" />
                                <DataRow label="Full Rx:" value={props.Rx} unit="(in)" />
                                <DataRow label="Full Iy:" value={props.Iy} unit="(in⁴)" />
                                <DataRow label="Full Syl:" value={props.Syl} unit="(in³)" />
                                <DataRow label="Full Syr:" value={props.Syr} unit="(in³)" />
                                <DataRow label="Full Ry:" value={props.Ry} unit="(in)" />
                                <DataRow label="Gross Area:" value={props.grossArea} unit="(in²)" />
                            </div>

                            {/* Right Column */}
                            <div>
                                <DataRow label="Net Area:" value={props.netArea} unit="(in²)" />
                                <DataRow label="C Corner:" value={props.cCorner} unit="(in)" />
                                <DataRow label="J:" value={props.J} unit="(in⁴)" />
                                <DataRow label="Xo:" value={props.Xo} unit="(in)" />
                                <DataRow label="jx:" value={props.jx} unit="(in)" />
                                <DataRow label="Ro:" value={props.Ro} unit="" />
                                <DataRow label="Beta:" value={props.Beta} unit="" />
                                <DataRow label="Cw:" value={props.Cw} unit="" />
                                <DataRow label="Weight:" value={props.weight} unit="(lbs/in)" />
                                <DataRow label="Fy:" value={props.Fy} unit="(ksi)" />
                                <DataRow label="Fu:" value={props.Fu} unit="(ksi)" />
                            </div>
                            </>
                            )}
                        </div>
                    )}
                </div>
                )}

            </div>
        </div>

        {/* RIGHT PANEL: Preview */}
        <div className={cn(
            "flex-1 bg-muted/30 flex flex-col relative overflow-hidden transition-all duration-300",
            isFullscreen ? "fixed inset-0 z-50 bg-background" : "h-1/2 lg:h-full"
        )}>
            
            {/* Watermark / Background Grid */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.4]"
                 style={{
                    backgroundImage: 'radial-gradient(circle, #a1a1aa 1px, transparent 1px)',
                    backgroundSize: '24px 24px'
                 }}
            />

            {/* Floating Toolbar */}
            <div className="absolute top-6 right-6 flex flex-col gap-2 z-10">
                <Button 
                    variant="secondary" size="icon" 
                    className="h-9 w-9 shadow-sm bg-background/80 backdrop-blur border border-border/50 hover:bg-background"
                    onClick={() => setIsFullscreen(!isFullscreen)}
                >
                     {isFullscreen ? <Minimize2 className="h-4 w-4 text-muted-foreground" /> : <Maximize2 className="h-4 w-4 text-muted-foreground" />}
                </Button>
                <div className="flex flex-col rounded-md shadow-sm bg-background/80 backdrop-blur border border-border/50 overflow-hidden">
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-none hover:bg-muted" onClick={handleZoomIn}>
                        <ZoomIn className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <div className="h-[1px] bg-border/50 mx-2" />
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-none hover:bg-muted" onClick={handleZoomOut}>
                        <ZoomOut className="h-4 w-4 text-muted-foreground" />
                    </Button>
                </div>
            </div>

            {/* Main Canvas */}
            <div className="flex-1 flex items-center justify-center p-8 md:p-12 min-h-0 container mx-auto relative z-0 overflow-hidden">
                 <div 
                    className="w-full h-full max-h-[600px] flex items-center justify-center bg-background/40 backdrop-blur-sm rounded-xl border border-secondary/20 shadow-sm p-8 transition-transform duration-200 ease-out"
                    style={{ transform: `scale(${zoom})` }}
                 >
                     <PartPreview type={type} geometry={geometry} />
                 </div>
            </div>

            {/* Bottom Actions Bar */}
            <div className="p-6 border-t border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex items-center justify-between gap-4">
                <div className="hidden md:flex flex-col">
                    <span className="text-xs font-medium text-foreground">Preview Mode</span>
                    <span className="text-[10px] text-muted-foreground">Scale: {(zoom * 100).toFixed(0)}% • {geometry.h}" x {geometry.w}"</span>
                </div>
                {isFullscreen && (
                     <Button variant="outline" onClick={() => setIsFullscreen(false)}>Close Fullscreen</Button>
                )}
                <Button className="w-full md:w-auto min-w-[160px] text-sm py-5 shadow-lg shadow-primary/20">
                    <Save className="mr-2 h-4 w-4" />
                    Save to library
                </Button>
            </div>
        </div>

      </div>
    </div>
  );
};

export default PartBuilder;
